import type { FC } from 'react'
import { memo } from 'react'

import { Flex, SubText, Text } from '@procurify/ui'
import * as Sentry from '@sentry/react'
import { Severity } from '@sentry/types'
import moment from 'moment'
import { useIntl } from 'react-intl'

import { toast } from '@webapp/components/Notification'
import { UserAvatar } from '@webapp/components/UserAvatar'
import { type Comment } from '@webapp/models/Comment'
import { getFullName } from '@webapp/utils/user'
import messages from './messages'
import { ChatCommentStyled } from './styles/ChatCommentStyled'

export interface IChatCommentProps {
  isReceiver?: boolean
  comment: Comment
}

export const ChatComment: FC<IChatCommentProps> = memo((props) => {
  const { comment, isReceiver } = props

  const intl = useIntl()

  const flexDirection = isReceiver ? 'row-reverse' : 'row'

  const renderCommentHTML = (unformattedComment: string) => {
    const matchedTags = unformattedComment.match(/<\s*[^>]*>(.*?)<\/\s*[^>]*>/g)

    if (!matchedTags) return unformattedComment

    const escapeTagForRegex = (tag: string) =>
      tag.replace(/[.+?^${}()|[\]\\]/g, String.raw`\$&`)

    try {
      if (matchedTags.find((tag) => tag.includes('*')))
        throw new Error('Username contains invalid character')

      const regexSplit = new RegExp(
        matchedTags.map((tag) => `(${escapeTagForRegex(tag)})`).join('|')
      )

      const formattedComment = unformattedComment
        .split(regexSplit)
        .map((substr, index) => {
          if (!substr) return ''

          const substrMatch = substr.match(/<\s*[^>]*>(.*?)<\/\s*[^>]*>/)
          if (!substrMatch || !substrMatch[1]) return substr

          return (
            <span key={index} className='mention'>
              {substrMatch[1]}
            </span>
          )
        })

      return formattedComment
    } catch (e) {
      toast.error({
        children: intl.formatMessage(messages.usernameParseError),
        title: 'Error',
      })

      Sentry.withScope((scope) => {
        scope.setLevel(Severity.Warning)
        Sentry.captureException({
          message: 'ChatComment: failed to format comment',
          originalException: e,
        })
      })
    }
  }

  return (
    <ChatCommentStyled>
      <Flex.Row flexWrap='nowrap' flexDirection={flexDirection}>
        <Flex.Item>
          <UserAvatar size='xsmall' url={comment.userprofile.profile_image} />
        </Flex.Item>
        <Flex.Item>
          <div className='comment'>
            <Text className='comment-header'>
              <span className='comment-author'>
                {getFullName(comment.userprofile)}
              </span>{' '}
              {intl.formatMessage(messages.wroteLabel)}:
            </Text>
            <Text className='comment-date'>
              {moment(comment.submit_date).format('lll')}
            </Text>
            <SubText>{renderCommentHTML(comment.comment)}</SubText>
          </div>
        </Flex.Item>
      </Flex.Row>
    </ChatCommentStyled>
  )
})

ChatComment.defaultProps = {
  isReceiver: false,
  comment: null,
}
ChatComment.displayName = 'ChatComment'
